import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Settings card exposing the user's GDPR / CCPA rights:
//   - Download my data (GET /api/privacy/export → JSON file)
//   - Delete my account (DELETE /api/privacy/account → wipes + signs out)

export const PrivacySettingsCard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/privacy/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bilateral-calm-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded.");
    } catch (e) {
      toast.error("Could not download your data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/privacy/account`, { withCredentials: true });
      toast.success("Your account has been deleted.");
      // Logout clears local state + redirects to /. Use a tiny delay so
      // the toast renders before navigation flushes.
      setTimeout(() => { logout(); }, 400);
    } catch (e) {
      toast.error("Could not delete your account. Please contact support.");
      setDeleting(false);
    }
  };

  return (
    <div
      id="delete-account"
      className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-6 animate-fade-in-up scroll-mt-24"
      data-testid="privacy-settings-card"
    >
      <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#3090A8]" />
        Privacy & account deletion
      </h2>
      <p className="text-sm text-[#718096] mb-5">
        Read our{" "}
        <button
          onClick={() => navigate("/privacy")}
          className="text-[#3090A8] underline underline-offset-2 hover:text-[#1B2349]"
          data-testid="privacy-policy-link"
        >
          privacy policy
        </button>
        , download a copy of your data, or permanently delete your account
        below.
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 text-[#1B2349] font-medium hover:bg-slate-50 disabled:opacity-60"
          data-testid="privacy-export-btn"
        >
          {exporting
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Download className="w-4 h-4" />}
          Download my data
        </button>
        <button
          onClick={() => setConfirming(true)}
          disabled={deleting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-[#D98880] text-[#D98880] font-medium hover:bg-[#FEF2F0] disabled:opacity-60"
          data-testid="privacy-delete-btn"
        >
          <Trash2 className="w-4 h-4" />
          Delete my account
        </button>
      </div>

      {confirming && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          role="button"
          tabIndex={0}
          onClick={() => !deleting && setConfirming(false)}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !deleting) setConfirming(false);
          }}
          data-testid="privacy-delete-confirm-modal"
        >
          <div
            className="bg-white rounded-2xl p-7 max-w-md w-full shadow-2xl"
            role="document"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h3 className="font-outfit font-semibold text-xl text-[#1B2349] mb-2">
              Delete your account?
            </h3>
            <p className="text-[#4A5568] leading-relaxed mb-6">
              This permanently removes your practice history, progress,
              favorites, reminders, and login. Your subscription will end.
              <br />
              <strong className="text-[#1B2349]">This cannot be undone.</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setConfirming(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-full border border-slate-200 text-[#1B2349] font-medium hover:bg-slate-50"
                data-testid="privacy-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#D98880] text-white font-medium hover:bg-[#C2786F] disabled:opacity-60"
                data-testid="privacy-delete-confirm"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Yes, delete everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacySettingsCard;
